import { MetadataMomentSystemName } from '../../gql/graphql';

const acceptedDomain = (acceptedDomains: string[]) => {
  const curDomain = currentDomain();
  for (const domain of acceptedDomains ?? []) {
    const sanitized = removeTrailingSlash(sanitizeString(domain));
    if (sanitized.includes('*')) {
      if (wildcardMatch(sanitized, curDomain)) return curDomain;
    } else if (curDomain.includes(sanitized)) {
      return domain;
    }
  }
  return undefined;
};

// Convert a `*.`-style wildcard origin (e.g. `https://*.example.com`) into a regex
// that matches the apex and any depth of subdomain, but not suffix-injected hosts.
const wildcardMatch = (pattern: string, origin: string) => {
  // Escape any regex special characters just in case
  const escaped = pattern.replace(/[*.+?^${}()|[\]\\]/g, '\\$&');
  const source = '^' + escaped.replace(/\\\*\\\./, '([^/.]+\\.)*') + '$';
  try {
    return new RegExp(source).test(origin);
  } catch {
    // Malformed pattern (e.g. multiple `*`s) — treat as no match so the caller can try the next entry.
    return false;
  }
};

const removeTrailingSlash = (url: string) => {
  let trimmedUrl = url;
  if (url.charAt(url.length - 1) === '/') {
    trimmedUrl = url.substring(0, url.length - 1);
  }
  return trimmedUrl;
};

const sanitizeString = (url: string) => {
  let sanitziedUrl = url;
  return sanitziedUrl.trim().toLowerCase();
};

const currentDomain = () => {
  // check to see if we have a parentDomain query param before trying to calculate it
  let parentDomainQueryParam = new URLSearchParams(document.location.search.substring(1)).get('parentDomain');
  if (parentDomainQueryParam) {
    parentDomainQueryParam = sanitizeString(parentDomainQueryParam);
    parentDomainQueryParam = removeTrailingSlash(parentDomainQueryParam);
    return parentDomainQueryParam;
  }

  let url = window.location !== window.parent.location ? document.referrer : window.location.origin;
  if (url.length === 0) {
    // Webkit-based browsers support ancestorOrigins for fetching the origin of the parent site
    const ancestorOrigin = window.location.ancestorOrigins?.[0];
    // When an ancestor frame has an opaque origin (e.g. a sandboxed iframe without allow-same-origin,
    // or one loaded from a data:/blob:/file: URL), ancestorOrigins reports the literal string 'null'.
    // Note: a no-referrer/Referrer-Policy does NOT affect ancestorOrigins — only document.referrer.
    if (ancestorOrigin && ancestorOrigin != 'null') {
      url = ancestorOrigin;
    } else {
      // We don't have a domain so just return an empty string
      window.analytics?.track('No Valid Origin Found');
      return '';
    }
  }
  const l = new URL(url);
  let sanitizedUrl = sanitizeString(l.origin);
  return removeTrailingSlash(sanitizedUrl);
};

// Can be used to check iframe's parent domain
const inIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

const getSystemName = (): MetadataMomentSystemName => (inIframe() ? MetadataMomentSystemName.UnifiedUiModal : MetadataMomentSystemName.UnifiedUiFullscreen);

const deleteQueryParam = (param: string) => {
  const url = new URL(window.location.href);
  url.searchParams.delete(param);
  window.history.replaceState({}, document.title, url.toString());
};

export { acceptedDomain, inIframe, getSystemName, currentDomain, deleteQueryParam };
